import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Paper,
  Box,
  Popper,
  Grow,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@material-ui/core'
import clsx from 'clsx'
import moment from 'moment'
import get from 'lodash/get'

import { useDispatch, useSelector } from 'react-redux'
import { getSelectedCollations } from 'selectors/ui'
import { getShipmentTours } from 'selectors/combined'
import { clearSelected } from 'actions/ui'
import { addTour, moveMultiple } from 'actions/tours'
import { isValidCollation } from 'actions/tours/helpers'
import { getLastStopTime, isPastDeadLine } from 'utils'
import { contrastColor } from 'common/utils/Color'

import { ShipmentTour } from 'common/models/typings/Tour'

import Button from 'common/components/base/Button'
import Typography from 'common/components/base/Typography'

import useStyles from './useStyles'
import { NETWORK_TYPE } from '../../../../models/typings/TourOverview'
import * as fromNetworkType from '../../../../../selectors/networkType'
import { Collation } from 'common/models/typings/Collation'
import { useAppDispatch } from 'planner-upgrades/redux/hooks'
import { Icon } from 'common/components/base/.raw'

interface TourMapActionsProps {
  setIsLoading: (isLoading: boolean) => void
}

const TourMapActions: React.FC<TourMapActionsProps> = ({ setIsLoading }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const classes = useStyles()

  const tours: ShipmentTour[] = useSelector(getShipmentTours)
  const selectedMarkers = useSelector(getSelectedCollations)
  const networkType: NETWORK_TYPE = useSelector(fromNetworkType.getNetworkType)

  const [anchor, setAnchor] = React.useState<HTMLButtonElement | null>(null)

  const handleToggleAnchor = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchor(anchor ? null : event.currentTarget)
    },
    [anchor],
  )

  const getStartTime = React.useCallback(
    (collations: Collation[]) =>
      moment(get(collations, '[0].estimation')).format('HH:mm'),
    [],
  )

  const getEndTime = React.useCallback((collations: Collation[]) => {
    return {
      endTime: moment(getLastStopTime(collations)).format('HH:mm'),
      pastDeadline: isPastDeadLine(collations, networkType),
    }
  }, [])

  const handleUnSelect = () => {
    dispatch(clearSelected())
  }

  const handleAddTour = React.useCallback(async () => {
    setIsLoading(true)
    try {
      await Promise.race([
        dispatch(addTour(selectedMarkers)),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        ),
      ])
      dispatch(clearSelected())
    } catch (error) {
    } finally {
      setIsLoading(false)
    }
  }, [dispatch, selectedMarkers, setIsLoading])

  const handleMoveCollations = React.useCallback(
    async (id: ShipmentTour['id']) => {
      setIsLoading(true)
      try {
        await Promise.race([
          dispatch(moveMultiple(id, selectedMarkers)),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Request timeout')), 10000)
          ),
        ])
        dispatch(clearSelected())
      } catch (error) {
        console.error('Move collations failed or timed out:', error)
      } finally {
        setIsLoading(false)
      }
    },
    [dispatch, selectedMarkers, setIsLoading],
  )

  const listItems = React.useMemo(
    () =>
      tours.map((tr, index) => (
        <ListItem
          key={tr.id}
          disableGutters
          button
          className={classes.listItem}
          onClick={() => handleMoveCollations(tr.id)}
        >
          <Box
            className={clsx(classes.listItemMarker, {
              [classes.borderTopLeft]: index === 0,
            })}
            style={{ background: tr.color, color: contrastColor(tr.color) }}
          >
            {tr.number}
          </Box>
          <Box width="32px" />
          <ListItemText
            primary={
              <>
                <Typography variant="packageText" color="indigo">
                  {getStartTime(tr.collations)}
                  {' - '}
                </Typography>
                <Typography variant="packageText" color="indigo">
                  {getEndTime(tr.collations).endTime}
                </Typography>
              </>
            }
            style={{ marginLeft: 32 }}
          />
          <ListItemSecondaryAction>
            <Typography variant="packageText" color="indigo">
              {tr.collations
                .filter(isValidCollation as any)
                .reduce((a, b) => a + b.shipmentIds.length || a, 0)}
            </Typography>
          </ListItemSecondaryAction>
        </ListItem>
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleMoveCollations, tours],
  )

  const open = Boolean(anchor)

  if (selectedMarkers.length < 1) return null

  return (
    <Paper className={classes.paper}>
      <Box className={classes.container}>
        <Box className={classes.indicator}>
          <Typography>{selectedMarkers.length}</Typography>
        </Box>
        <Button
          color="action"
          onClick={handleToggleAnchor}
          className={classes.button}
        >
          {t('MOVE_TO_DIFFERENT_TOUR')}
        </Button>
        <Button
          color="danger"
          onClick={handleUnSelect}
          className={classes.button}
        >
          {t('DE_SELECT_ALL')}
        </Button>
      </Box>
      <Popper
        open={open}
        anchorEl={anchor}
        placement="top"
        transition
        className={classes.popper}
        style={{ zIndex: 2000 }}
        disablePortal={false}
        modifiers={{
          preventOverflow: {
            enabled: true,
            boundariesElement: 'viewport',
          },
        }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={350}>
            <Paper className={classes.popperPaper}>
              <Box className={classes.listContainer}>
                <List disablePadding>{listItems}</List>
              </Box>
              <Button
                variant="flat"
                color="white"
                fullWidth
                onClick={handleAddTour}
              >
                {t('ADD_TO_NEW_TOUR')}
              </Button>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Paper>
  )
}

export default React.memo(TourMapActions)
